import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { ScrollText, ArrowLeft, Check, AlertCircle, CheckCircle2 } from 'lucide-react';
import useBlinkHighlight from '../../hooks/useBlinkHighlight';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};
const formatDateTime = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const PortalMemos = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(null);
  const memoRefs = useRef({});
  // Scroll-and-blink the memo card targeted by a notification deep-link
  // (e.g. /employee-portal/memos?highlight=<memo_id>).
  useBlinkHighlight(memoRefs, !loading && memos.length > 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/employee-portal/memos`, { headers });
      setMemos(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/employee-portal/login');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (id) => {
    setAcking(id);
    try {
      await axios.post(`${API}/api/employee-portal/memos/${id}/read`, {}, { headers });
      const now = new Date().toISOString();
      setMemos(prev => prev.map(m => m.id === id ? { ...m, is_read: true, read_at: now } : m));
      toast({ title: 'Acknowledged', description: 'You can revisit this memo anytime.' });
    } catch {
      toast({ title: 'Failed', variant: 'destructive' });
    } finally { setAcking(null); }
  };

  const pendingCount = memos.filter(m => !m.is_read).length;

  return (
    <div className="p-4 max-w-3xl mx-auto" data-testid="portal-memos">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>

      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
          <ScrollText className="h-6 w-6 text-indigo-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">Company Memos</h1>
          <p className="text-xs text-gray-500">{memos.length} memo{memos.length === 1 ? '' : 's'} · {pendingCount} pending</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-rose-500 text-white" data-testid="pending-memos-badge">
            <AlertCircle className="h-3 w-3 mr-1" /> {pendingCount} new
          </Badge>
        )}
      </div>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-gray-500">Loading…</CardContent></Card>
      ) : memos.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <ScrollText className="h-12 w-12 mx-auto text-indigo-300 mb-3" />
            <p className="text-gray-600">No memos published yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {memos.map(m => (
            <MemoPage
              key={m.id}
              memo={m}
              cardRef={(el) => { memoRefs.current[m.id] = el; }}
              onAcknowledge={() => acknowledge(m.id)}
              acking={acking === m.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Memo Page (inline rendering, view-only) ────────────────────────
const MemoPage = ({ memo, onAcknowledge, acking, cardRef }) => {
  const inlineHtml = memo.rendered_html || memo.content || '';
  const isPdf = memo.file_kind === 'pdf' && memo.file_url;
  const isImage = memo.file_kind === 'image' && memo.file_url;

  return (
    <Card
      ref={cardRef}
      className={`border-0 shadow-sm overflow-hidden ${memo.is_read ? 'bg-white' : 'bg-amber-50/40 ring-1 ring-amber-200'}`}
      data-testid={`memo-card-${memo.id}`}
    >
      {/* Header */}
      <div className="p-5 border-b">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <Badge variant="outline" className="text-[10px]">{memo.category}</Badge>
          <span className="text-[11px] text-gray-400">{formatDate(memo.created_at)}</span>
          {memo.is_read ? (
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid={`memo-read-${memo.id}`}>
              <CheckCircle2 className="h-3 w-3 mr-0.5" /> Read on {formatDateTime(memo.read_at)}
            </Badge>
          ) : (
            <Badge className="bg-amber-500 text-white text-[10px]">New</Badge>
          )}
        </div>
        <h2 className="text-lg font-bold text-gray-900">{memo.title}</h2>
      </div>

      {/* Body — view-only, scrollable, PDF-page styled */}
      <div className="p-3 sm:p-4 bg-gray-100 max-h-[80vh] overflow-y-auto" data-testid={`memo-body-${memo.id}`}>
        {inlineHtml && (
          <div className="memo-page bg-white shadow-md rounded-md mx-auto p-6 sm:p-10" style={{ maxWidth: '780px', minHeight: '600px' }}>
            <style>{`
              .memo-page .memo-content img {
                max-width: 180px;
                height: auto;
                display: block;
                margin: 0 auto 1.25rem auto;
              }
              .memo-page .memo-content p { margin: 0.5rem 0; line-height: 1.55; }
              .memo-page .memo-content h1,
              .memo-page .memo-content h2,
              .memo-page .memo-content h3 { margin: 0.75rem 0 0.5rem; }
              .memo-page .memo-content ol,
              .memo-page .memo-content ul { padding-left: 1.4rem; margin: 0.5rem 0; }
              .memo-page .memo-content table { border-collapse: collapse; margin: 0.75rem 0; }
              .memo-page .memo-content table td,
              .memo-page .memo-content table th { border: 1px solid #d1d5db; padding: 6px 10px; }
            `}</style>
            <div
              className="memo-content prose prose-sm max-w-none text-gray-800"
              dangerouslySetInnerHTML={{ __html: inlineHtml }}
            />
          </div>
        )}
        {isPdf && (
          <div className="rounded-lg overflow-hidden border bg-white" style={{ height: '80vh' }}>
            <iframe
              src={`${API}${memo.file_url}#toolbar=0`}
              title={memo.title}
              className="w-full h-full"
              data-testid={`memo-pdf-${memo.id}`}
            />
          </div>
        )}
        {isImage && (
          <img
            src={`${API}${memo.file_url}`}
            alt={memo.title}
            className="w-full max-w-[780px] mx-auto rounded-md border bg-white shadow-md"
            data-testid={`memo-image-${memo.id}`}
          />
        )}
        {!inlineHtml && !isPdf && !isImage && (
          <p className="text-sm text-gray-400 italic text-center py-12">No content provided.</p>
        )}
      </div>

      {/* Footer — acknowledge gate */}
      <div className="p-4 border-t bg-gray-50/60">
        {!memo.is_read ? (
          <Button
            onClick={onAcknowledge}
            disabled={acking}
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            data-testid={`memo-ack-btn-${memo.id}`}
          >
            <Check className="h-4 w-4 mr-1" /> {acking ? 'Saving…' : 'I have read & understood'}
          </Button>
        ) : (
          <div className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium">
            <CheckCircle2 className="h-4 w-4" /> Acknowledged on {formatDateTime(memo.read_at)}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PortalMemos;
